import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import Topbar from "@/components/Topbar";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, Search, History, Loader2 } from "lucide-react";

const hoyISO = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Mexico_City" });
const UB = { almacen: "Almacén", camara_fria: "Cámara fría", barra: "Barra" };
const lineaVacia = () => ({ ubicacion: "almacen", supply_id: "", cantidad: "" });
const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fb;
};

// Fix WH92 (2026-11-03): el insumo se busca escribiendo, sin importar acentos ni
// mayúsculas, en vez de un desplegable sin búsqueda. Salen tres grupos: lo habitual de la
// sucursal, el resto del catálogo y los productos del punto de venta. Si no hay
// coincidencia exacta («chila jalapeño»), muestra lo que coincide con alguna palabra.
const normInsumo = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/-/g, " ").replace(/\s+/g, " ").trim();
const GRUPOS_INSUMO = [["habitual", "De esta sucursal"], ["catalogo", "Resto del catálogo"], ["pos", "Punto de venta"]];
const MAX_POR_GRUPO = 60;

function SelectorInsumo({ testid, catalogo, value, nombreGuardado, onChange, className }) {
  const ref = useRef(null);
  const [q, setQ] = useState("");
  const [abierto, setAbierto] = useState(false);
  const opciones = useMemo(() => [
    ...(catalogo.insumos || []).map((o) => ({ ...o, grupo: o.habitual ? "habitual" : "catalogo" })),
    ...(catalogo.productos_pos || []).map((o) => ({ ...o, grupo: "pos" })),
  ].map((o) => ({ ...o, clave: normInsumo(o.name) })), [catalogo]);
  const elegido = opciones.find((o) => o.id === value);
  const etiqueta = elegido ? `${elegido.name}${elegido.unit ? ` (${elegido.unit})` : ""}` : (value ? nombreGuardado || "" : "");
  const { lista, parecidos } = useMemo(() => {
    const palabras = normInsumo(q).split(" ").filter(Boolean);
    if (!palabras.length) return { lista: opciones, parecidos: false };
    const todas = opciones.filter((o) => palabras.every((w) => o.clave.includes(w)));
    if (todas.length) return { lista: todas, parecidos: false };
    const largas = palabras.filter((w) => w.length >= 3);
    return { lista: opciones.filter((o) => largas.some((w) => o.clave.includes(w))), parecidos: true };
  }, [q, opciones]);
  const elegir = (o) => { onChange(o.id); setQ(""); setAbierto(false); ref.current?.blur(); };
  return (
    <div className={`relative min-w-0 ${className || ""}`}>
      <input ref={ref} data-testid={testid} value={abierto ? q : etiqueta} placeholder={(abierto && etiqueta) || "Buscar insumo…"}
        onFocus={() => { setQ(""); setAbierto(true); }} onClick={() => setAbierto(true)} onBlur={() => setAbierto(false)}
        onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && abierto && lista[0]) { e.preventDefault(); elegir(lista[0]); }
          if (e.key === "Escape") ref.current?.blur();
        }}
        className="border rounded-lg px-2 py-2 text-sm w-full" />
      {abierto && (
        <div data-testid={`${testid}-opciones`} className="absolute z-30 left-0 mt-1 w-full min-w-[16rem] max-h-72 overflow-y-auto bg-white border rounded-lg shadow-lg text-sm">
          {!opciones.length && <p className="p-2 text-slate-500">Elige primero la sucursal</p>}
          {!!opciones.length && !lista.length && <p className="p-2 text-slate-500">Sin coincidencias</p>}
          {parecidos && !!lista.length && <p className="px-2 pt-2 text-xs text-amber-700">Sin coincidencia exacta; estos se parecen:</p>}
          {GRUPOS_INSUMO.map(([g, titulo]) => {
            const items = lista.filter((o) => o.grupo === g);
            if (!items.length) return null;
            return (
              <div key={g} data-testid={`${testid}-grupo-${g}`}>
                <p className="px-2 pt-2 pb-1 text-[11px] font-bold uppercase text-slate-500">{titulo} ({items.length})</p>
                {items.slice(0, MAX_POR_GRUPO).map((o) => (
                  <button type="button" key={o.id} data-testid={`${testid}-opcion`} onMouseDown={(e) => e.preventDefault()} onClick={() => elegir(o)}
                    className={`block w-full text-left px-2 py-1.5 hover:bg-amber-50 ${o.id === value ? "font-bold" : ""}`}>
                    {o.name}{o.unit ? <span className="text-slate-400"> ({o.unit})</span> : null}
                  </button>
                ))}
                {items.length > MAX_POR_GRUPO && <p className="px-2 pb-1 text-xs text-slate-400">y {items.length - MAX_POR_GRUPO} más: escribe para filtrar</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function InventarioDiario() {
  const { user } = useAuth();
  const esGerente = user?.role === "branch_manager";
  const [branches, setBranches] = useState([]);
  const [filtro, setFiltro] = useState({ branch_id: "", desde: "", hasta: "", folio: "" });
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [catalogo, setCatalogo] = useState({ insumos: [], productos_pos: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches((r.data || []).filter((b) => !b.is_corporate && b.active !== false)));
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtro).filter(([, v]) => v));
      const r = await api.get("/inventario-diario", { params });
      setLista(r.data);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cargar el inventario diario"));
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const abrir = async (id) => {
    try { setSel((await api.get(`/inventario-diario/${id}`)).data); setForm(null); }
    catch (e) { toast.error(errMsg(e, "No se pudo abrir la captura")); }
  };

  const cargarCatalogo = async (branch_id) => {
    if (!branch_id) return setCatalogo({ insumos: [], productos_pos: [] });
    try { setCatalogo((await api.get("/inventario-diario/catalogo", { params: { branch_id } })).data); }
    catch (e) { setCatalogo({ insumos: [], productos_pos: [] }); toast.error(errMsg(e, "No se pudo cargar el catálogo")); }
  };

  const nueva = () => {
    const branch_id = esGerente ? user.branch_id : "";
    setSel(null);
    setForm({ modo: "crear", branch_id, fecha: hoyISO(), folio: "", responsable: "", motivo: "", lineas: [lineaVacia()] });
    cargarCatalogo(branch_id);
  };

  const editar = () => {
    setForm({ modo: "editar", id: sel.id, version: sel.version, branch_id: sel.branch_id, fecha: sel.fecha, folio: sel.folio,
      responsable: sel.responsable, motivo: "",
      lineas: sel.lineas.map((l) => ({ ubicacion: l.ubicacion, supply_id: l.supply_id, insumo: l.insumo, cantidad: String(l.cantidad) })) });
    cargarCatalogo(sel.branch_id);
  };

  const setLinea = (i, k, v) => setForm((f) => ({ ...f, lineas: f.lineas.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));

  const guardar = async () => {
    if (form.lineas.some((l) => !l.supply_id || l.cantidad === "" || Number.isNaN(Number(l.cantidad)))) {
      return toast.error("Completa insumo y cantidad en todas las líneas");
    }
    const lineas = form.lineas.map((l) => ({ ubicacion: l.ubicacion, supply_id: l.supply_id, cantidad: Number(l.cantidad) }));
    setSaving(true);
    try {
      const r = form.modo === "crear"
        ? await api.post("/inventario-diario", { branch_id: form.branch_id, fecha: form.fecha, folio: form.folio, responsable: form.responsable, lineas })
        : await api.put(`/inventario-diario/${form.id}`, { version: form.version, motivo: form.motivo, responsable: form.responsable, lineas });
      toast.success(form.modo === "crear" ? "Captura guardada" : "Cambio guardado");
      setForm(null);
      await cargar();
      await abrir(r.data.id);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo guardar"));
      const ex = e?.response?.data?.detail?.existente;
      if (e?.response?.status === 409 && ex?.id) await abrir(ex.id);
    } finally {
      setSaving(false);
    }
  };

  const nombreSuc = (id) => branches.find((b) => b.id === id)?.name || id;

  return (
    <>
      <Topbar title="Inventario diario" subtitle="Captura por sucursal, fecha y folio, con historial de cambios" />
      <div className="p-6 space-y-5" data-testid="inventario-diario-page">
        <div className="flex flex-wrap gap-2 items-end">
          {!esGerente && (
            <select data-testid="invd-filtro-sucursal" value={filtro.branch_id} onChange={(e) => setFiltro({ ...filtro, branch_id: e.target.value })}
              className="border rounded-lg px-3 py-2 text-sm">
              <option value="">Todas las sucursales</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <input data-testid="invd-filtro-desde" type="date" value={filtro.desde} onChange={(e) => setFiltro({ ...filtro, desde: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
          <input data-testid="invd-filtro-hasta" type="date" value={filtro.hasta} onChange={(e) => setFiltro({ ...filtro, hasta: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
          <input data-testid="invd-filtro-folio" placeholder="Folio" value={filtro.folio} onChange={(e) => setFiltro({ ...filtro, folio: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
          <button data-testid="invd-buscar-btn" onClick={cargar} className="btn-primary-chizy px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Consultar
          </button>
          <button data-testid="invd-nueva-btn" onClick={nueva} className="px-4 py-2 rounded-xl text-sm font-bold border inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva captura
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="border rounded-xl overflow-hidden" data-testid="invd-lista">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left"><tr>
                <th className="p-2">Fecha</th><th className="p-2">Folio</th><th className="p-2">Sucursal</th><th className="p-2">Responsable</th><th className="p-2">Ver.</th>
              </tr></thead>
              <tbody>
                {lista.map((c) => (
                  <tr key={c.id} data-testid={`invd-fila-${c.id}`} onClick={() => abrir(c.id)} className="border-t cursor-pointer hover:bg-slate-50">
                    <td className="p-2">{c.fecha}</td><td className="p-2">{c.folio}</td><td className="p-2">{c.branch_name || nombreSuc(c.branch_id)}</td>
                    <td className="p-2">{c.responsable}</td><td className="p-2">v{c.version}</td>
                  </tr>
                ))}
                {!lista.length && <tr><td colSpan={5} className="p-4 text-center text-slate-500">Sin capturas para estos filtros</td></tr>}
              </tbody>
            </table>
          </div>

          <div>
            {form && (
              <div className="border rounded-xl p-4 space-y-3" data-testid="invd-form">
                <h3 className="font-bold">{form.modo === "crear" ? "Nueva captura" : `Editar ${form.folio} (${form.fecha})`}</h3>
                {form.modo === "crear" && (
                  <div className="grid grid-cols-3 gap-2">
                    <select data-testid="invd-form-sucursal" disabled={esGerente} value={form.branch_id}
                      onChange={(e) => { setForm({ ...form, branch_id: e.target.value, lineas: [lineaVacia()] }); cargarCatalogo(e.target.value); }}
                      className="border rounded-lg px-2 py-2 text-sm">
                      <option value="">Sucursal…</option>
                      {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    <input data-testid="invd-form-fecha" type="date" max={hoyISO()} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="border rounded-lg px-2 py-2 text-sm" />
                    <input data-testid="invd-form-folio" placeholder="Folio" value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} className="border rounded-lg px-2 py-2 text-sm" />
                  </div>
                )}
                <input data-testid="invd-form-responsable" placeholder="Responsable" value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} className="border rounded-lg px-2 py-2 text-sm w-full" />
                {form.lineas.map((l, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)_auto] gap-2" data-testid={`invd-linea-${i}`}>
                    <select data-testid={`invd-linea-${i}-ubicacion`} value={l.ubicacion} onChange={(e) => setLinea(i, "ubicacion", e.target.value)} className="border rounded-lg px-2 py-2 text-sm w-full min-w-0">
                      {Object.entries(UB).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <SelectorInsumo testid={`invd-linea-${i}-insumo`} catalogo={catalogo} value={l.supply_id} nombreGuardado={l.insumo}
                      onChange={(v) => setLinea(i, "supply_id", v)} className="col-span-3 sm:col-span-1 order-first sm:order-none" />
                    <input data-testid={`invd-linea-${i}-cantidad`} type="number" min="0" step="any" placeholder="Cantidad" value={l.cantidad} onChange={(e) => setLinea(i, "cantidad", e.target.value)} className="border rounded-lg px-2 py-2 text-sm w-full min-w-0" />
                    <button data-testid={`invd-linea-${i}-quitar`} aria-label="Quitar línea" onClick={() => setForm({ ...form, lineas: form.lineas.filter((_, j) => j !== i) })} className="px-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button data-testid="invd-agregar-linea" onClick={() => setForm({ ...form, lineas: [...form.lineas, lineaVacia()] })} className="text-sm font-bold inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Agregar línea</button>
                {form.modo === "editar" && (
                  <input data-testid="invd-form-motivo" placeholder="Motivo del cambio (obligatorio)" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="border rounded-lg px-2 py-2 text-sm w-full" />
                )}
                <div className="flex gap-2">
                  <button data-testid="invd-guardar-btn" disabled={saving} onClick={guardar} className="btn-primary-chizy px-4 py-2 rounded-xl text-sm font-bold">{saving ? "Guardando…" : "Guardar"}</button>
                  <button data-testid="invd-cancelar-btn" onClick={() => setForm(null)} className="px-4 py-2 rounded-xl text-sm border">Cancelar</button>
                </div>
              </div>
            )}

            {sel && !form && (
              <div className="border rounded-xl p-4 space-y-3" data-testid="invd-detalle">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold">{sel.folio} · {sel.fecha}</h3>
                    <p className="text-xs text-slate-500">{sel.branch_name} · Responsable: {sel.responsable} · Cargó: {sel.created_by?.email} · v{sel.version}</p>
                  </div>
                  <button data-testid="invd-editar-btn" onClick={editar} className="px-3 py-1.5 rounded-lg text-sm font-bold border">Editar</button>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-left bg-slate-50"><tr><th className="p-1">Ubicación</th><th className="p-1">Insumo</th><th className="p-1">Cantidad</th></tr></thead>
                  <tbody>{sel.lineas.map((l, i) => (
                    <tr key={i} className="border-t"><td className="p-1">{UB[l.ubicacion]}</td><td className="p-1">{l.insumo}{l.origen === "punto_de_venta" && <span className="ml-1 text-[10px] text-slate-400">punto de venta</span>}</td><td className="p-1">{l.cantidad} {l.unidad || ""}</td></tr>
                  ))}</tbody>
                </table>
                <h4 className="font-bold text-sm inline-flex items-center gap-1"><History className="w-4 h-4" /> Historial</h4>
                <ul className="text-xs space-y-1" data-testid="invd-historial">
                  {sel.historial.map((h) => (
                    <li key={h.id} className="border-l-2 pl-2">
                      v{h.version_nueva} · {h.accion === "creacion" ? "Creación" : "Edición"} · {h.user?.email} · {h.timestamp}
                      {h.motivo ? ` · Motivo: ${h.motivo}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
